import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  ArrowLeft,
  ArrowRight,
  Copy,
  FileText,
  Calendar,
  DollarSign,
  Building,
  MapPin,
  CreditCard,
  History,
  Link as LinkIcon,
  Download,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertCircle,
  Filter,
  ArrowUpDown,
  Zap,
  Receipt,
  Activity,
  Layers,
  Home,
  Wallet
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { format } from 'date-fns';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { generateDummyBill } from './dummyBillData';

export default function TumBillDetail() {
  const { billId, workspaceId } = useParams<{ billId: string; workspaceId?: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const activeWorkspaceId = workspaceId || currentWorkspace?.id;
  const [activeTab, setActiveTab] = useState('full-bill');
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfZoom, setPdfZoom] = useState(100);
  
  // Use dummy data for demo/preview
  const isDemo = billId === 'demo' || billId === '1096901';
  const dummyBill = isDemo ? generateDummyBill() : null;

  // Fetch bill details
  const { data: fetchedBill, isLoading } = useQuery({
    queryKey: ['tum-bill-detail', billId, currentWorkspace?.id],
    queryFn: async () => {
      if (!billId || !currentWorkspace?.id) return null;
      
      const { data, error } = await supabase
        .from('bills')
        .select(`
          *,
          property:tum_properties(
            id,
            property_name,
            address,
            property_type
          ),
          utility_account:tum_utility_accounts(
            id,
            account_number,
            commodity,
            vendor_id,
            vendor:tum_utility_vendors(
              id,
              vendor_name,
              customer_service_phone
            )
          ),
          unit:tum_units(
            id,
            unit_number,
            unit_name
          )
        `)
        .eq('id', billId)
        .eq('workspace_id', currentWorkspace.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!billId && !!currentWorkspace?.id && !isDemo,
  });

  // Use dummy bill for demo, otherwise use fetched bill
  const bill = isDemo ? dummyBill : fetchedBill;

  // Fetch bill chain (related bills)
  const { data: fetchedBillChain } = useQuery({
    queryKey: ['tum-bill-chain', bill?.utility_account?.id, currentWorkspace?.id],
    queryFn: async () => {
      if (!bill?.utility_account?.id || !currentWorkspace?.id) return [];
      
      const { data, error } = await supabase
        .from('bills')
        .select('*')
        .eq('tum_utility_account_id', bill.utility_account.id)
        .eq('workspace_id', currentWorkspace.id)
        .order('bill_date', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data || [];
    },
    enabled: !!bill?.utility_account?.id && !!currentWorkspace?.id && !isDemo,
  });

  // Use dummy data for demo
  const billChain = isDemo ? (dummyBill?.related_bills || []) : fetchedBillChain;

  // Fetch account history (bills for the same account)
  const { data: fetchedAccountHistory } = useQuery({
    queryKey: ['tum-account-history', bill?.utility_account?.id, currentWorkspace?.id],
    queryFn: async () => {
      if (!bill?.utility_account?.id || !currentWorkspace?.id) return [];
      
      const { data, error } = await supabase
        .from('bills')
        .select('*')
        .eq('tum_utility_account_id', bill.utility_account.id)
        .eq('workspace_id', currentWorkspace.id)
        .order('bill_date', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
    enabled: !!bill?.utility_account?.id && !!currentWorkspace?.id && !isDemo,
  });

  // Use dummy data for demo
  const accountHistory = isDemo ? (dummyBill?.account_history || []) : fetchedAccountHistory;

  // Fetch bill allocations
  const { data: allocations } = useQuery({
    queryKey: ['tum-bill-allocations', billId, currentWorkspace?.id],
    queryFn: async () => {
      if (!billId || !currentWorkspace?.id) return [];
      
      const { data, error } = await supabase
        .from('tum_bill_allocations')
        .select(`
          *,
          tenant:tum_tenants(id, display_name),
          unit:tum_units(id, unit_number)
        `)
        .eq('bill_id', billId)
        .eq('workspace_id', currentWorkspace.id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!billId && !!currentWorkspace?.id,
  });

  const formatCurrency = (amount: number | string | null) => {
    if (!amount) return '$0.00';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num || 0);
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MM/dd/yyyy');
    } catch {
      return 'N/A';
    }
  };

  const formatDateTime = (date: string | Date | null) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MM/dd/yyyy | HH:mm');
    } catch {
      return 'N/A';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <Clock className="h-8 w-8 mx-auto mb-2 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading bill details...</p>
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
          <p className="text-muted-foreground">Bill not found</p>
          <Button 
            onClick={() => {
              if (activeWorkspaceId) {
                navigate(`/w/${activeWorkspaceId}/dashboard/tum/bills`);
              } else {
                navigate('/tum/bills');
              }
            }} 
            className="mt-4"
          >
            Back to Bills
          </Button>
        </div>
      </div>
    );
  }

  const billAmount = parseFloat(bill.amount?.toString() || '0');
  const amountDue = parseFloat(bill.amount_due?.toString() || bill.amount?.toString() || '0');
  const pastDue = 0; // This would come from bill data

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Enhanced Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (activeWorkspaceId) {
                navigate(`/w/${activeWorkspaceId}/dashboard/tum/bills`);
              } else {
                navigate('/tum/bills');
              }
            }}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Return to Bills</span>
          </Button>
        </div>
        
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-4xl font-bold tracking-tight">
                  Invoice #{isDemo && dummyBill?.bill_number 
                    ? dummyBill.bill_number 
                    : bill.bill_number || bill.id.slice(0, 8)}
                </h1>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="outline" className="gap-1">
                    <Building className="h-3 w-3" />
                    {isDemo && dummyBill?.property?.property_name
                      ? dummyBill.property.property_name
                      : bill.property?.property_name || 'N/A'}
                  </Badge>
                  <Badge variant="outline" className="gap-1">
                    <Zap className="h-3 w-3" />
                    {isDemo && dummyBill?.utility_account?.commodity
                      ? dummyBill.utility_account.commodity.toUpperCase()
                      : (bill.utility_account?.commodity || 'N/A').toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              <span>Previous</span>
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <span>Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Enhanced Summary Bar */}
      <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-primary/20 shadow-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <p className="text-sm font-medium">Payment Due Date</p>
              </div>
              <p className="text-2xl font-bold">{formatDate(bill.due_date)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Receipt className="h-4 w-4" />
                <p className="text-sm font-medium">Current Charges</p>
              </div>
              <p className="text-2xl font-bold text-primary">{formatCurrency(billAmount)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                <p className="text-sm font-medium">Outstanding Balance</p>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(pastDue)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Wallet className="h-4 w-4" />
                <p className="text-sm font-medium">Total Amount Due</p>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(amountDue)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Bill Information */}
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Invoice Details</CardTitle>
              <CardDescription>Comprehensive billing and account information</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 flex-1">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Billing ID:</span>
              </div>
              <span className="font-medium font-mono">
                {isDemo && dummyBill?.utility_account?.account_number
                  ? dummyBill.utility_account.account_number
                  : bill.bill_number || bill.id.slice(0, 8)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => copyToClipboard(
                  isDemo && dummyBill?.utility_account?.account_number
                    ? dummyBill.utility_account.account_number
                    : bill.bill_number || bill.id
                )}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <span className="text-muted-foreground">Vendor Name:</span>
                <span className="font-medium ml-2">
                  {isDemo && dummyBill?.utility_account?.vendor?.vendor_name
                    ? dummyBill.utility_account.vendor.vendor_name
                    : bill.utility_account?.vendor?.vendor_name || 'N/A'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <span className="text-muted-foreground">Vendor ID:</span>
                <span className="font-medium ml-2 font-mono text-xs">
                  {isDemo && dummyBill?.utility_account?.vendor?.id
                    ? `${dummyBill.utility_account.vendor.id} (Provider Ven...)`
                    : bill.utility_account?.vendor?.id?.slice(0, 8) || 'N/A'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <span className="text-muted-foreground">Invoice Date:</span>
                <span className="font-medium ml-2">
                  {isDemo && dummyBill?.bill_date
                    ? formatDate(dummyBill.bill_date)
                    : formatDate(bill.bill_date)}
                </span>
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Utilities:</span>
              <span className="font-medium ml-2 capitalize">
                {isDemo && dummyBill?.bill_blocks
                  ? dummyBill.bill_blocks.map((b: any) => b.service_type || b.rate_plan).filter((v: any, i: number, arr: any[]) => arr.indexOf(v) === i).join(', ')
                  : bill.utility_account?.commodity || 'N/A'} 
                {isDemo && dummyBill?.bill_blocks ? ` Total Accounts ${dummyBill.bill_blocks.length}` : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <span className="text-muted-foreground">Total Accounts:</span>
                <Badge variant="secondary" className="ml-2">
                  {isDemo && dummyBill?.bill_blocks ? dummyBill.bill_blocks.length : 1}
                </Badge>
              </div>
            </div>
            {((isDemo && dummyBill?.utility_account?.vendor?.customer_service_phone) || 
              (bill.utility_account as any)?.vendor?.customer_service_phone) && (
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <span className="text-muted-foreground">Vendor Main Phone:</span>
                  <span className="font-medium ml-2 font-mono">
                    {isDemo && dummyBill?.utility_account?.vendor?.customer_service_phone
                      ? dummyBill.utility_account.vendor.customer_service_phone
                      : (bill.utility_account as any).vendor.customer_service_phone}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Content with Tabs and PDF Viewer */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Tabs */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-0">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="border-b border-border">
                  <TabsList className="w-full h-auto p-0 bg-transparent justify-start overflow-x-auto flex-nowrap gap-1">
                    <TabsTrigger 
                      value="full-bill" 
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
                    >
                      <Receipt className="h-4 w-4" />
                      <span className="hidden sm:inline">Invoice Details</span>
                      <span className="sm:hidden">Details</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="summary" 
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
                    >
                      <Activity className="h-4 w-4" />
                      <span className="hidden sm:inline">Processing Timeline</span>
                      <span className="sm:hidden">Timeline</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="history" 
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
                    >
                      <History className="h-4 w-4" />
                      <span className="hidden sm:inline">Account History</span>
                      <span className="sm:hidden">History</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="chain" 
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
                    >
                      <Layers className="h-4 w-4" />
                      <span className="hidden sm:inline">Invoice Chain</span>
                      <span className="sm:hidden">Chain</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="addresses" 
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
                    >
                      <MapPin className="h-4 w-4" />
                      <span className="hidden sm:inline">Addresses</span>
                      <span className="sm:hidden">Address</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="account" 
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
                    >
                      <Home className="h-4 w-4" />
                      <span className="hidden sm:inline">Service Account</span>
                      <span className="sm:hidden">Account</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="payment" 
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
                    >
                      <CreditCard className="h-4 w-4" />
                      <span className="hidden sm:inline">Payment Status</span>
                      <span className="sm:hidden">Payment</span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Full Bill Tab */}
                <TabsContent value="full-bill" className="p-6 space-y-6">
                  <div className="flex items-center justify-between border-b pb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Layers className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold">Service Line Items</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {isDemo ? (dummyBill?.bill_blocks?.length || 0) : 1} service block{(isDemo ? (dummyBill?.bill_blocks?.length || 0) : 1) !== 1 ? 's' : ''} found
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="gap-2">
                        <Filter className="h-4 w-4" />
                        Filter Items
                      </Button>
                      <Button variant="outline" size="sm" className="gap-2">
                        <ArrowUpDown className="h-4 w-4" />
                        Sort
                      </Button>
                    </div>
                  </div>

                  {/* Bill Blocks */}
                  {isDemo && dummyBill?.bill_blocks ? (
                    dummyBill.bill_blocks.map((block: any, index: number) => {
                      const getServiceIcon = (serviceType: string) => {
                        switch (serviceType?.toUpperCase()) {
                          case 'WATER':
                            return <Zap className="h-5 w-5 text-blue-500" />;
                          case 'SEWER':
                            return <FileText className="h-5 w-5 text-green-500" />;
                          case 'ELECTRIC':
                            return <Zap className="h-5 w-5 text-yellow-500" />;
                          case 'GAS':
                            return <Zap className="h-5 w-5 text-orange-500" />;
                          default:
                            return <Zap className="h-5 w-5 text-muted-foreground" />;
                        }
                      };
                      
                      return (
                        <Card key={block.id || index} className="border-l-4 border-l-primary/50">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {getServiceIcon(block.service_type || block.rate_plan)}
                                <CardTitle className="text-base capitalize">
                                  {block.service_type || block.rate_plan || 'Utility'}
                                </CardTitle>
                              </div>
                              <Button variant="ghost" size="sm">More</Button>
                            </div>
                          </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <FileText className="h-3 w-3" />
                                <p className="text-xs font-medium">Meter ID</p>
                              </div>
                              <p className="font-semibold">{block.meter_id || block.meter_number || 'N/A'}</p>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <CreditCard className="h-3 w-3" />
                                <p className="text-xs font-medium">Service Account</p>
                              </div>
                              <p className="font-semibold">{bill.utility_account?.account_number || 'N/A'}</p>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Receipt className="h-3 w-3" />
                                <p className="text-xs font-medium">Rate Plan</p>
                              </div>
                              <p className="font-semibold">{block.rate_plan || 'Standard'}</p>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <p className="text-xs font-medium">Service Period</p>
                              </div>
                              <p className="font-semibold text-xs">
                                {formatDate(block.service_start_date)} - {formatDate(block.service_end_date)}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Activity className="h-3 w-3" />
                                <p className="text-xs font-medium">Consumption</p>
                              </div>
                              <p className="font-semibold">
                                {block.consumption || 'N/A'} {block.consumption_unit || ''}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <DollarSign className="h-3 w-3" />
                                <p className="text-xs font-medium">Charges</p>
                              </div>
                              <p className="font-semibold text-primary">{formatCurrency(block.charges || 0)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                    })
                  ) : (
                    <Card className="border-l-4 border-l-primary/50">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {bill.utility_account?.commodity === 'water' && <Zap className="h-5 w-5 text-blue-500" />}
                            {bill.utility_account?.commodity === 'electric' && <Zap className="h-5 w-5 text-yellow-500" />}
                            {bill.utility_account?.commodity === 'gas' && <Zap className="h-5 w-5 text-orange-500" />}
                            {!['water', 'electric', 'gas'].includes(bill.utility_account?.commodity || '') && (
                              <Zap className="h-5 w-5 text-muted-foreground" />
                            )}
                            <CardTitle className="text-base capitalize">
                              {bill.utility_account?.commodity || 'Utility'}
                            </CardTitle>
                          </div>
                          <Button variant="ghost" size="sm">More</Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <FileText className="h-3 w-3" />
                              <p className="text-xs font-medium">Meter ID</p>
                            </div>
                            <p className="font-semibold">N/A</p>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <CreditCard className="h-3 w-3" />
                              <p className="text-xs font-medium">Service Account</p>
                            </div>
                            <p className="font-semibold">{bill.utility_account?.account_number || 'N/A'}</p>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Receipt className="h-3 w-3" />
                              <p className="text-xs font-medium">Rate Plan</p>
                            </div>
                            <p className="font-semibold">Standard</p>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <p className="text-xs font-medium">Service Period</p>
                            </div>
                            <p className="font-semibold text-xs">
                              {formatDate(bill.billing_period_start)} - {formatDate(bill.billing_period_end)}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Activity className="h-3 w-3" />
                              <p className="text-xs font-medium">Consumption</p>
                            </div>
                            <p className="font-semibold">{bill.usage_amount || 'N/A'} {bill.usage_unit || ''}</p>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <DollarSign className="h-3 w-3" />
                              <p className="text-xs font-medium">Charges</p>
                            </div>
                            <p className="font-semibold text-primary">{formatCurrency(billAmount)}</p>
                          </div>
                        </div>

                        <Separator className="my-4" />

                        {/* General Information */}
                        <div className="space-y-2">
                          <h4 className="font-semibold">General Information</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Bill Subtotal:</p>
                              <p className="font-medium">{formatCurrency(billAmount)}</p>
                            </div>
                            {isDemo && dummyBill?.account_details?.virtual_account_id && (
                              <div>
                                <p className="text-muted-foreground">Virtual Account ID:</p>
                                <p className="font-medium">
                                  {dummyBill.account_details.virtual_account_id} (Account History)
                                </p>
                              </div>
                            )}
                            <div>
                              <p className="text-muted-foreground">Service Account ID:</p>
                              <p className="font-medium">{bill.utility_account?.account_number || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Service Address:</p>
                              <p className="font-medium">
                                {isDemo && dummyBill?.account_details?.service_address
                                  ? dummyBill.account_details.service_address
                                  : bill.property?.address && typeof bill.property.address === 'object' 
                                    ? (bill.property.address as any).street || 'N/A'
                                    : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Service Zip:</p>
                              <p className="font-medium">
                                {isDemo && dummyBill?.account_details?.service_zip
                                  ? dummyBill.account_details.service_zip
                                  : bill.property?.address && typeof bill.property.address === 'object'
                                    ? (bill.property.address as any).postal_code || 'N/A'
                                    : 'N/A'}
                              </p>
                            </div>
                            {isDemo && dummyBill?.bill_blocks?.[0]?.meter_id && (
                              <div>
                                <p className="text-muted-foreground">Meter ID:</p>
                                <p className="font-medium">{dummyBill.bill_blocks[0].meter_id}</p>
                              </div>
                            )}
                            {isDemo && dummyBill?.account_details?.delivery_location && (
                              <div>
                                <p className="text-muted-foreground">Delivery Location:</p>
                                <p className="font-medium">{dummyBill.account_details.delivery_location}</p>
                              </div>
                            )}
                            {isDemo && dummyBill?.account_details?.vendor_type && (
                              <div>
                                <p className="text-muted-foreground">Vendor Type:</p>
                                <p className="font-medium">{dummyBill.account_details.vendor_type}</p>
                              </div>
                            )}
                            {isDemo && dummyBill?.account_details?.location_address && (
                              <div>
                                <p className="text-muted-foreground">Location Address:</p>
                                <p className="font-medium">{dummyBill.account_details.location_address}</p>
                              </div>
                            )}
                            {isDemo && dummyBill?.account_details?.location_description && (
                              <div>
                                <p className="text-muted-foreground">Location Description:</p>
                                <p className="font-medium">{dummyBill.account_details.location_description}</p>
                              </div>
                            )}
                            {isDemo && dummyBill?.account_details?.division && (
                              <div>
                                <p className="text-muted-foreground">Division:</p>
                                <p className="font-medium">{dummyBill.account_details.division}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-muted-foreground">Service Start Date:</p>
                              <p className="font-medium">
                                {isDemo && dummyBill?.bill_blocks?.[0]?.service_start_date
                                  ? formatDate(dummyBill.bill_blocks[0].service_start_date)
                                  : formatDate(bill.billing_period_start)}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Service End Date:</p>
                              <p className="font-medium">
                                {isDemo && dummyBill?.bill_blocks?.[0]?.service_end_date
                                  ? formatDate(dummyBill.bill_blocks[0].service_end_date)
                                  : formatDate(bill.billing_period_end)}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Days of Service:</p>
                              <p className="font-medium">
                                {isDemo && dummyBill?.bill_blocks?.[0]?.days_of_service
                                  ? `${dummyBill.bill_blocks[0].days_of_service} (E-S)`
                                  : bill.billing_period_start && bill.billing_period_end
                                    ? Math.ceil(
                                        (new Date(bill.billing_period_end).getTime() - 
                                         new Date(bill.billing_period_start).getTime()) / 
                                        (1000 * 60 * 60 * 24)
                                      )
                                    : 'N/A'}
                              </p>
                            </div>
                            {isDemo && dummyBill?.account_details?.next_read_date && (
                              <div>
                                <p className="text-muted-foreground">Next Read Date:</p>
                                <p className="font-medium">{formatDate(dummyBill.account_details.next_read_date)}</p>
                              </div>
                            )}
                            {isDemo && dummyBill?.account_details?.prior_meter_reading && (
                              <div>
                                <p className="text-muted-foreground">Prior Meter Reading:</p>
                                <p className="font-medium">{dummyBill.account_details.prior_meter_reading}</p>
                              </div>
                            )}
                            {isDemo && dummyBill?.bill_blocks?.[0]?.rate_plan && (
                              <div>
                                <p className="text-muted-foreground">Rate Plan:</p>
                                <p className="font-medium">{dummyBill.bill_blocks[0].rate_plan}</p>
                              </div>
                            )}
                            {isDemo && dummyBill?.account_details?.estimated !== undefined && (
                              <div>
                                <p className="text-muted-foreground">Estimated:</p>
                                <p className="font-medium">{dummyBill.account_details.estimated ? 'Yes' : 'No'}</p>
                              </div>
                            )}
                          </div>
                        </div>

                    <Separator className="my-4" />

                    {/* Usage Information */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-primary" />
                        <h4 className="font-semibold text-base">Usage Information</h4>
                      </div>
                      <Card className="bg-muted/30">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Activity className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Total Consumption:</span>
                            </div>
                            <p className="font-semibold text-lg">
                              {isDemo && dummyBill?.bill_blocks?.[0]?.consumption
                                ? `${dummyBill.bill_blocks[0].consumption} ${dummyBill.bill_blocks[0].consumption_unit || ''}`
                                : `${bill.usage_amount || 'N/A'} ${bill.usage_unit || ''}`}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Bill Summary Tab */}
                <TabsContent value="summary" className="p-6 space-y-6">
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Activity className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold">Processing Timeline</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Track the complete lifecycle of this invoice from receipt to payment
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">Invoice Number:</p>
                            <p className="text-sm text-muted-foreground">
                              {isDemo ? (dummyBill?.invoice_number || '-') : (bill.invoice_number || '-')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">Bill Source:</p>
                            <p className="text-sm text-muted-foreground">
                              {isDemo ? 'Web download' : (bill.collection_status || 'Web download')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">Date Bill Received:</p>
                            <p className="text-sm text-muted-foreground">
                              {isDemo && dummyBill?.created_at 
                                ? formatDate(dummyBill.created_at)
                                : formatDate(bill.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">Date Loaded:</p>
                            <p className="text-sm text-muted-foreground">
                              {isDemo && dummyBill?.parsed_at
                                ? formatDateTime(dummyBill.parsed_at)
                                : formatDateTime(bill.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">Date Processed:</p>
                            <p className="text-sm text-muted-foreground">
                              {isDemo && dummyBill?.parsed_at
                                ? formatDateTime(dummyBill.parsed_at)
                                : formatDateTime(bill.parsed_at || bill.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">Date Marked for Payment:</p>
                            <p className="text-sm text-muted-foreground">
                              {isDemo && dummyBill?.payment_info?.marked_for_payment_date
                                ? formatDate(dummyBill.payment_info.marked_for_payment_date)
                                : formatDate(bill.due_date)}
                            </p>
                          </div>
                        </div>
                        {isDemo && dummyBill?.assigned_at && (
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">Date Fund Request Sent by A/P File:</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDateTime(dummyBill.assigned_at)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <Separator />

                    {/* Bill Comparison */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Layers className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold">Period Comparison</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Compare this invoice with previous and upcoming billing periods
                      </p>
                      <div className="grid grid-cols-4 gap-4">
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-sm">Approx. Last Year</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-muted-foreground text-sm">-</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-sm">Prior Bill</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-1">
                            <p className="text-xs text-muted-foreground">Bill ID</p>
                            <p className="font-medium text-sm">
                              {isDemo && dummyBill?.prior_bill?.bill_number
                                ? dummyBill.prior_bill.bill_number
                                : '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">Invoice Date</p>
                            <p className="font-medium text-sm">
                              {isDemo && dummyBill?.prior_bill?.invoice_date
                                ? formatDate(dummyBill.prior_bill.invoice_date)
                                : '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">Due Date</p>
                            <p className="font-medium text-sm">
                              {isDemo && dummyBill?.prior_bill?.due_date
                                ? formatDate(dummyBill.prior_bill.due_date)
                                : '-'}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="border-primary">
                          <CardHeader>
                            <CardTitle className="text-sm">Current Bill</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-1">
                            <p className="text-xs text-muted-foreground">Bill ID</p>
                            <p className="font-medium text-sm">{bill.bill_number || bill.id.slice(0, 8)}</p>
                            <p className="text-xs text-muted-foreground">Invoice Date</p>
                            <p className="font-medium text-sm">{formatDate(bill.bill_date)}</p>
                            <p className="text-xs text-muted-foreground">Due Date</p>
                            <p className="font-medium text-sm">{formatDate(bill.due_date)}</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-sm">Next Bill</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-muted-foreground text-sm">Estimated Invoice</p>
                            <p className="text-xs text-muted-foreground mt-2">-</p>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Account History Tab */}
                <TabsContent value="history" className="p-6 space-y-6">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between border-b pb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <History className="h-5 w-5 text-primary" />
                          <h3 className="text-lg font-semibold">Billing History</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {isDemo && dummyBill?.bill_blocks ? dummyBill.bill_blocks.length : 1} service account{dummyBill?.bill_blocks?.length !== 1 ? 's' : ''} associated
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          placeholder="mm/dd/yyyy"
                          className="w-40"
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                          type="date"
                          placeholder="mm/dd/yyyy"
                          className="w-40"
                        />
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {isDemo && dummyBill?.bill_blocks?.[0] ? (
                          <>
                            Account MID: {dummyBill.bill_blocks[0].meter_id || 'N/A'}, 
                            SAID: {bill.utility_account?.account_number || 'N/A'}, 
                            Utility: {dummyBill.bill_blocks[0].service_type || bill.utility_account?.commodity || 'N/A'}, 
                            Type: Full Service
                          </>
                        ) : (
                          <>
                            Account MID: N/A, SAID: {bill.utility_account?.account_number || 'N/A'}, 
                            Utility: {bill.utility_account?.commodity || 'N/A'}, Type: Full Service
                          </>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {isDemo && dummyBill?.account_details?.virtual_account_id ? (
                          <>
                            Meter reading for Virtual Account ID {dummyBill.account_details.virtual_account_id}
                          </>
                        ) : null}
                        <br />
                        Location: {isDemo && dummyBill?.account_details?.location_description
                          ? dummyBill.account_details.location_description
                          : bill.property?.property_name || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Bill Blocks total: {accountHistory?.length || 0}</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Bill ID</TableHead>
                            <TableHead>Start Date</TableHead>
                            <TableHead>End Date</TableHead>
                            <TableHead>Days Of Service</TableHead>
                            <TableHead>Subcharges</TableHead>
                            <TableHead>Consumption</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {accountHistory && accountHistory.length > 0 ? (
                            accountHistory.map((historyBill: any) => (
                              <TableRow
                                key={historyBill.id || historyBill.bill_number}
                                className="cursor-pointer"
                                onClick={() => {
                                  if (isDemo) {
                                    // In demo mode, just show the same bill
                                    return;
                                  }
                                  if (activeWorkspaceId) {
                                    navigate(`/w/${activeWorkspaceId}/dashboard/tum/bills/${historyBill.id}`);
                                  } else {
                                    navigate(`/tum/bills/${historyBill.id}`);
                                  }
                                }}
                              >
                                <TableCell className="font-medium">
                                  {historyBill.bill_number || historyBill.id?.slice(0, 8) || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  {isDemo && historyBill.billing_period_start
                                    ? formatDate(historyBill.billing_period_start)
                                    : formatDate(historyBill.billing_period_start || historyBill.bill_date)}
                                </TableCell>
                                <TableCell>
                                  {isDemo && historyBill.billing_period_end
                                    ? formatDate(historyBill.billing_period_end)
                                    : formatDate(historyBill.billing_period_end || historyBill.due_date)}
                                </TableCell>
                                <TableCell>
                                  {isDemo && historyBill.days_of_service
                                    ? historyBill.days_of_service
                                    : historyBill.billing_period_start && historyBill.billing_period_end
                                      ? Math.ceil(
                                          (new Date(historyBill.billing_period_end).getTime() - 
                                           new Date(historyBill.billing_period_start).getTime()) / 
                                          (1000 * 60 * 60 * 24)
                                        )
                                      : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  {formatCurrency(isDemo ? (historyBill.charges || 0) : (historyBill.amount || 0))}
                                </TableCell>
                                <TableCell>
                                  {isDemo && historyBill.consumption
                                    ? `${historyBill.consumption} ${historyBill.consumption_unit || ''}`
                                    : `${historyBill.usage_amount || 'N/A'} ${historyBill.usage_unit || ''}`}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                                No history found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </TabsContent>

                {/* Bill Chain Tab */}
                <TabsContent value="chain" className="p-6 space-y-6">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between border-b pb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Layers className="h-5 w-5 text-primary" />
                          <h3 className="text-lg font-semibold">Invoice Chain</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Chain ID: <span className="font-mono font-medium">{isDemo && dummyBill?.bill_chain_id
                            ? dummyBill.bill_chain_id
                            : bill.id.slice(0, 8)}</span> • Related invoices in sequence
                        </p>
                      </div>
                      <Button variant="outline" size="sm">
                        <Filter className="h-4 w-4 mr-2" />
                        Filters
                      </Button>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Bills total: {billChain?.length || 0}</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Bill ID</TableHead>
                            <TableHead>Invoice Date</TableHead>
                            <TableHead>Current Charges</TableHead>
                            <TableHead>Past Due</TableHead>
                            <TableHead>Total Amount Due</TableHead>
                            <TableHead>Pay Amount</TableHead>
                            <TableHead>Due Date</TableHead>
                            <TableHead>Days Since Previous Bill</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {billChain && billChain.length > 0 ? (
                            billChain.map((chainBill: any, index: number) => {
                              const isCurrentBill = isDemo 
                                ? chainBill.bill_number === dummyBill?.bill_number
                                : chainBill.id === billId;
                              
                              const prevBill = billChain[index + 1];
                              let daysSince: string | number;
                              
                              if (isDemo && chainBill.days_since_previous) {
                                daysSince = chainBill.days_since_previous;
                              } else if (prevBill) {
                                const currentBill = chainBill as any;
                                const prevBillAny = prevBill as any;
                                const currentDate = isDemo 
                                  ? chainBill.invoice_date 
                                  : (currentBill.bill_date || currentBill.invoice_date);
                                const prevDate = isDemo 
                                  ? prevBill.invoice_date 
                                  : (prevBillAny.bill_date || prevBillAny.invoice_date);
                                if (currentDate && prevDate) {
                                  daysSince = Math.ceil(
                                    (new Date(currentDate).getTime() - 
                                     new Date(prevDate).getTime()) / 
                                    (1000 * 60 * 60 * 24)
                                  );
                                } else {
                                  daysSince = index === billChain.length - 1 ? 'First Bill' : 0;
                                }
                              } else {
                                daysSince = index === billChain.length - 1 ? 'First Bill' : 0;
                              }

                              return (
                                <TableRow
                                  key={chainBill.id || chainBill.bill_number}
                                  className={`cursor-pointer ${isCurrentBill ? 'bg-primary/10' : ''}`}
                                  onClick={() => {
                                    if (isDemo) {
                                      // In demo mode, just show the same bill
                                      return;
                                    }
                                    if (activeWorkspaceId) {
                                      navigate(`/w/${activeWorkspaceId}/dashboard/tum/bills/${chainBill.id}`);
                                    } else {
                                      navigate(`/tum/bills/${chainBill.id}`);
                                    }
                                  }}
                                >
                                  <TableCell className="font-medium">
                                    {chainBill.bill_number || chainBill.id?.slice(0, 8) || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(isDemo ? chainBill.invoice_date : (chainBill.bill_date || chainBill.invoice_date))}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(isDemo ? chainBill.amount : (chainBill.amount || 0))}
                                  </TableCell>
                                  <TableCell>$0.00</TableCell>
                                  <TableCell>
                                    {formatCurrency(isDemo ? chainBill.amount_due : (chainBill.amount_due || chainBill.amount || 0))}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(isDemo ? chainBill.amount_due : (chainBill.amount_due || chainBill.amount || 0))}
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(isDemo ? chainBill.due_date : chainBill.due_date)}
                                  </TableCell>
                                  <TableCell>{daysSince}</TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                No related bills found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </TabsContent>

                {/* Billing Addresses Tab */}
                <TabsContent value="addresses" className="p-6 space-y-6">
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">Billing Addresses</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Bill-to and remittance addresses for this invoice
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <Card className="border-l-4 border-l-blue-500">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Building className="h-4 w-4" />
                          Billing Address
                        </CardTitle>
                        <CardDescription>Where invoices are sent</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <Label className="text-sm text-muted-foreground">Address Line 1</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.bill_to?.address_line_1
                              ? dummyBill.billing_addresses.bill_to.address_line_1
                              : bill.property?.address && typeof bill.property.address === 'object'
                                ? (bill.property.address as any).street || 'N/A'
                                : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Address Line 2</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.bill_to?.address_line_2
                              ? dummyBill.billing_addresses.bill_to.address_line_2 || '-'
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">City</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.bill_to?.city
                              ? dummyBill.billing_addresses.bill_to.city
                              : bill.property?.address && typeof bill.property.address === 'object'
                                ? (bill.property.address as any).city || 'N/A'
                                : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">State</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.bill_to?.state
                              ? dummyBill.billing_addresses.bill_to.state
                              : bill.property?.address && typeof bill.property.address === 'object'
                                ? (bill.property.address as any).state || 'N/A'
                                : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Postcode</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.bill_to?.postcode
                              ? dummyBill.billing_addresses.bill_to.postcode
                              : bill.property?.address && typeof bill.property.address === 'object'
                                ? (bill.property.address as any).postal_code || 'N/A'
                                : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Country</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.bill_to?.country
                              ? dummyBill.billing_addresses.bill_to.country || '-'
                              : '-'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-green-500">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Remittance Address
                        </CardTitle>
                        <CardDescription>Where payments should be sent</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <Label className="text-sm text-muted-foreground">Address Line 1</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.remittance?.address_line_1
                              ? dummyBill.billing_addresses.remittance.address_line_1
                              : bill.utility_account?.vendor?.vendor_name || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Address Line 2</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.remittance?.address_line_2
                              ? dummyBill.billing_addresses.remittance.address_line_2 || '-'
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">City</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.remittance?.city
                              ? dummyBill.billing_addresses.remittance.city
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">State</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.remittance?.state
                              ? dummyBill.billing_addresses.remittance.state
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Postcode</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.remittance?.postcode
                              ? dummyBill.billing_addresses.remittance.postcode
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Country</Label>
                          <p className="text-sm font-medium">
                            {isDemo && dummyBill?.billing_addresses?.remittance?.country
                              ? dummyBill.billing_addresses.remittance.country || '-'
                              : '-'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Billing Account Tab */}
                <TabsContent value="account" className="p-6 space-y-6">
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Home className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">Service Account Information</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Complete account details and service configuration
                    </p>
                  </div>
                  <Card className="border-l-4 border-l-purple-500">
                    <CardHeader>
                      <CardTitle className="text-base">Account Configuration</CardTitle>
                      <CardDescription>Service account settings and billing cycle</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm text-muted-foreground">Account Number</Label>
                          <p className="text-sm font-medium">{bill.utility_account?.account_number || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Commodity</Label>
                          <p className="text-sm font-medium capitalize">{bill.utility_account?.commodity || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Vendor</Label>
                          <p className="text-sm font-medium">{bill.utility_account?.vendor?.vendor_name || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Property</Label>
                          <p className="text-sm font-medium">{bill.property?.property_name || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Unit</Label>
                          <p className="text-sm font-medium">
                            {bill.unit?.unit_number || bill.unit?.unit_name || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Bill Cycle</Label>
                          <p className="text-sm font-medium">30</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Payment Information Tab */}
                <TabsContent value="payment" className="p-6 space-y-6">
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold">Payment Status & Processing</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Current payment status and batch processing information
                    </p>
                  </div>
                  <Card className="border-l-4 border-l-orange-500">
                    <CardHeader>
                      <CardTitle className="text-base">Payment Details</CardTitle>
                      <CardDescription>Status and processing information for this invoice</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-muted-foreground">Payment Batch Identifier</Label>
                          <div className="flex items-center gap-2">
                            <p className="text-base font-mono font-medium">
                              {isDemo && dummyBill?.payment_info?.payment_batch_id
                                ? dummyBill.payment_info.payment_batch_id
                                : 'Not Assigned'}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-muted-foreground">Current Status</Label>
                          <div>
                            <Badge 
                              variant={isDemo && dummyBill?.payment_info?.payment_status === 'PENDING' 
                                ? 'secondary' 
                                : 'default'}
                              className="text-sm px-3 py-1"
                            >
                              {isDemo && dummyBill?.payment_info?.payment_status
                                ? dummyBill.payment_info.payment_status
                                : 'PENDING'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - PDF Viewer */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Tabs defaultValue="pdf" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="pdf" className="text-xs">PDF Bill</TabsTrigger>
                    <TabsTrigger value="activity" className="text-xs">Activity</TabsTrigger>
                    <TabsTrigger value="changelog" className="text-xs">Changelog</TabsTrigger>
                    <TabsTrigger value="errors" className="text-xs">Errors</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* PDF Controls */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPdfPage(prev => Math.max(1, prev - 1))}
                      disabled={pdfPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {pdfPage} of 1
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPdfPage(prev => prev + 1)}
                      disabled={pdfPage >= 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPdfZoom(prev => Math.max(50, prev - 10))}
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground w-12 text-center">
                      {pdfZoom}%
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPdfZoom(prev => Math.min(200, prev + 10))}
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* PDF Placeholder */}
                <div className="border rounded-lg bg-muted/50 aspect-[8.5/11] flex items-center justify-center">
                  <div className="text-center space-y-2">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">PDF Viewer</p>
                    <p className="text-xs text-muted-foreground">
                      {bill.pdf_url ? 'PDF available' : 'No PDF available'}
                    </p>
                    {bill.pdf_url && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={bill.pdf_url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4 mr-2" />
                          Open PDF
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

